import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Bookmark, Heart } from "lucide-react";
import { useAuth } from "@/components/AuthProvider";
import DocumentTitle from "@/components/DocumentTitle";
import { CompactCard, VerticalCard, useWidthBand, PosterViewer } from "./feedCard";
import useSafeBack from "@/hooks/useSafeBack";

const API = process.env.REACT_APP_BACKEND_URL;
const client = axios.create({ baseURL: `${API}/api`, withCredentials: true });

/**
 * Vania Msg 215 §1 · saved / liked pages. Wired to the top-bar icons.
 * Guests see a sign-in prompt. Signed-in members see their list or an
 * empty state with a real Discover CTA (never a blank screen).
 */
export function SavedPage()  { return <TinyList mode="saved" />; }
export function LikedPage()  { return <TinyList mode="liked" />; }

function TinyList({ mode }) {
  const { user, loading } = useAuth();
  const [rows, setRows] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [viewer, setViewer] = useState(null);
  const { band, cols } = useWidthBand();
  const onBack = useSafeBack("/app");
  const Card = band === "s" || band === "m" ? CompactCard : VerticalCard;

  useEffect(() => {
    if (!user) { setFetching(false); return; }
    setFetching(true);
    client.get(`/me/${mode === "saved" ? "saved" : "liked"}`)
      .then((r) => setRows(r.data.events || []))
      .catch(() => setRows([]))
      .finally(() => setFetching(false));
  }, [user, mode]);

  const Icon = mode === "saved" ? Bookmark : Heart;
  const title = mode === "saved" ? "saved" : "liked";
  const emptyLine = mode === "saved"
    ? "nothing saved yet. tap the bookmark on any event to keep it here."
    : "nothing liked yet. tap the heart on any event or post you like.";

  return (
    <div className="flex h-full flex-col" data-testid={`${mode}-page`} style={{ background: "var(--ww-ground)", color: "var(--ww-text)" }}>
      <DocumentTitle title={`${title} · watu wako`} />
      <div className="flex-1 overflow-y-auto px-5 pt-4 pb-10" style={{ overscrollBehavior: "contain" }}>
        <div className="mx-auto" style={{ maxWidth: 1360 }}>
          <button type="button" onClick={onBack} data-testid={`${mode}-back`} aria-label="back"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--ww-text-muted)",
                           fontFamily: "Arial", fontSize: 13, padding: "10px 4px", minHeight: 44,
                           background: "transparent", border: 0, cursor: "pointer" }}>
            <ArrowLeft size={16} /> back
          </button>
          <h1 data-testid={`${mode}-title`} className="mt-2 lowercase"
              style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.025em",
                       fontSize: 34, lineHeight: 1.02 }}>
            {title}
          </h1>

          {(loading || fetching) && <p className="mt-10 text-center" style={{ fontFamily: "Arial", color: "var(--ww-text-muted)" }}>loading…</p>}

          {!loading && !user && (
            <EmptyBlock testId={`${mode}-signin`} icon={<Icon size={28} color="#FFD60A" />}
                        line={`sign in to see what you have ${title}.`}
                        ctaHref={`/admin/login?next=/app/${mode === "saved" ? "saved" : "liked"}`}
                        ctaLabel="sign in" />
          )}

          {!loading && user && !fetching && rows.length === 0 && (
            <EmptyBlock testId={`${mode}-empty`} icon={<Icon size={28} color="#FFD60A" />}
                        line={emptyLine}
                        ctaHref="/app/discover" ctaLabel="open discover" />
          )}

          {!fetching && rows.length > 0 && (
            <div style={{ marginTop: 20, display: "grid",
                          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                          gap: cols === 1 ? 12 : 16 }}>
              {rows.map((e) => <Card key={e.id} event={e} onOpenPoster={setViewer} />)}
            </div>
          )}
        </div>
      </div>
      {viewer && <PosterViewer viewer={viewer} onClose={() => setViewer(null)} />}
    </div>
  );
}

function EmptyBlock({ line, ctaHref, ctaLabel, testId, icon }) {
  return (
    <div className="mt-10 rounded-2xl border p-8 text-center" data-testid={testId}
         style={{ borderColor: "var(--ww-line)", background: "var(--ww-surface)" }}>
      <div style={{ marginBottom: 12 }}>{icon}</div>
      <p style={{ fontFamily: "Arial", color: "var(--ww-text-muted)", fontSize: 15 }}>{line}</p>
      <Link to={ctaHref} data-testid={`${testId}-cta`}
            style={{ display: "inline-block", marginTop: 14, padding: "10px 20px", borderRadius: 999,
                     background: "#FFD60A", color: "#1A1A30",
                     fontFamily: "Poppins", fontWeight: 700, textDecoration: "none", textTransform: "lowercase" }}>
        {ctaLabel}
      </Link>
    </div>
  );
}
